import React, { useState, useEffect } from 'react';
import {
  RefreshCw, Copy, CheckCheck, Shield, Cpu, Eye, EyeOff,
  Calendar, Users,
} from 'lucide-react';
import { usersAPI } from '../../services/api';
import { useRoleData } from '../../hooks/useRoleData';
import { useAppContext } from '../../context/AppContext';
import { usePersistentTab } from '../../hooks/usePersistentTab';
import UsersTab from './UsersTab';
import './Settings.css';
import { formatDate } from '../../utils/dateFormatter';


const Settings = () => {
  const { showNotification, currentUser } = useAppContext();
  const { filteredUsers, loading, error, refreshData } = useRoleData();
  const [activeTab, setActiveTab] = usePersistentTab('tokens');
  const [tokens, setTokens] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);

  // Transform filteredUsers into tokens format
  useEffect(() => {
    if (filteredUsers.length > 0) {
      const newTokens = filteredUsers.map((user) => ({
        userId: user.id,
        userName: user.name,
        userRole: user.role,
        token: user.token || '',
        issuedDate: user.joinDate?.split('T')[0] || '2024-01-15',
        status: user.status === 'Active' ? 'Active' : 'Inactive',
        visible: false,
        userDepartment: user.department || '',
        userSpecialization: user.specialization || '',
        userInitials: user.initials || '',
      }));
      setTokens(newTokens);
    }
  }, [filteredUsers]);

  const handleReissue = async (userId) => {
    setGeneratingId(userId);
    try {
      await usersAPI.updateToken(userId);
      showNotification('Token re-issued successfully!', 'success');
      refreshData(); // This will trigger useEffect and update tokens
    } catch (error) {
      console.error('Failed to re-issue token:', error);
      showNotification(error.response?.data?.error || 'Failed to re-issue token', 'error');
    } finally {
      setGeneratingId(null);
    }
  };

  const handleCopy = (token, userId) => {
    navigator.clipboard.writeText(token).catch(() => {});
    setCopiedId(userId);
    setTimeout(() => setCopiedId(null), 2000);
    showNotification('Token copied to clipboard', 'info');
  };

  const toggleVisibility = (userId) => {
    setTokens((prev) =>
      prev.map((t) => (t.userId === userId ? { ...t, visible: !t.visible } : t))
    );
  };

  const trainees = tokens.filter((t) => t.userRole === 'Trainee');
  const instructors = tokens.filter((t) => t.userRole === 'Instructor');

  return (
    <div className="settings">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1>Settings</h1>
          <p>Manage VR access tokens and system preferences</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="settings__tabs">
        {[
          ...(currentUser?.role === 'Administrator' ? [{ id: 'users', label: 'Users', icon: Users }] : []),
          { id: 'tokens', label: 'VR Access Tokens', icon: Shield },
          { id: 'system', label: 'System', icon: Cpu },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            className={`settings__tab ${activeTab === id ? 'active' : ''}`}
            onClick={() => setActiveTab(id)}
          >
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'users' && currentUser?.role === 'Administrator' && <UsersTab />}

      {activeTab === 'tokens' && (
        <div className="settings__tokens animate-fade-in">
          {/* Info Banner */}
          <div className="settings__token-info">
            <div className="settings__token-info-icon">
              <Shield size={20} />
            </div>
            <div>
              <h4>VR Device Login Tokens</h4>
              <p>
                Each user is assigned a unique 4-character alphanumeric token to authenticate
                from their VR headset. Tokens are case-sensitive and expire only when re-issued.
              </p>
            </div>
          </div>

          {loading && (
            <div className="text-center py-8">
              <p>Loading tokens...</p>
            </div>
          )}
          {error && (
            <div className="alert alert-error">
              <p>Error loading tokens: {error}</p>
              <button className="btn btn-outline btn-sm mt-2" onClick={refreshData}>Retry</button>
            </div>
          )}

          {!loading && !error && (
            <>
              {/* Trainees Tokens */}
              {currentUser?.role === 'Administrator' && (
                <div className="card">
                  <div className="card-header">
                    <h3>Trainee Tokens</h3>
                    <span className="badge badge-green">{trainees.length} users</span>
                  </div>
                  <div className="settings__token-table">
                    <div className="settings__token-header">
                      <span>User</span>
                      <span>Department / Role</span>
                      <span>VR Token</span>
                      <span>Issued Date</span>
                      <span>Status</span>
                      <span>Actions</span>
                    </div>
                    {trainees.map((t) => {
                      return (
                        <div key={t.userId} className="settings__token-row">
                          <div className="settings__token-user">
                            <div
                              className="avatar"
                              style={{ width: 34, height: 34, fontSize: '0.7rem' }}
                            >
                              {t.userInitials}
                            </div>
                            <span className="settings__token-name">{t.userName}</span>
                          </div>
                          <span className="settings__token-dept">{t.userDepartment}</span>
                          <div className="settings__token-value">
                            <div className="settings__token-display">
                              {t.visible ? (
                                <span className="settings__token-chars">{t.token}</span>
                              ) : (
                                <span className="settings__token-hidden">••••</span>
                              )}
                              <button
                                className="btn-icon"
                                onClick={() => toggleVisibility(t.userId)}
                              >
                                {t.visible ? <EyeOff size={13} /> : <Eye size={13} />}
                              </button>
                            </div>
                          </div>
                          <span className="settings__token-date">
                            <Calendar size={12} />
                            {formatDate(t.issuedDate)}
                          </span>
                          <span className={`badge ${t.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>
                            {t.status}
                          </span>
                          <div className="settings__token-actions">
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => handleCopy(t.token, t.userId)}
                              title="Copy token"
                            >
                              {copiedId === t.userId ? (
                                <CheckCheck size={13} style={{ color: 'var(--color-lime)' }} />
                              ) : (
                                <Copy size={13} />
                              )}
                            </button>
                            <button
                              className="btn btn-outline btn-sm"
                              onClick={() => handleReissue(t.userId)}
                              title="Re-issue token"
                              disabled={generatingId === t.userId}
                            >
                              <RefreshCw
                                size={13}
                                className={generatingId === t.userId ? 'settings__spin' : ''}
                              />
                              Re-issue
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Instructors Tokens */}
              <div className="card">
                <div className="card-header">
                  <h3>Instructor Tokens</h3>
                  <span className="badge badge-blue">{instructors.length} users</span>
                </div>
                <div className="settings__token-table">
                  <div className="settings__token-header">
                    <span>User</span>
                    <span>Specialization</span>
                    <span>VR Token</span>
                    <span>Issued Date</span>
                    <span>Status</span>
                    <span>Actions</span>
                  </div>
                  {instructors.map((t) => {
                    return (
                      <div key={t.userId} className="settings__token-row">
                        <div className="settings__token-user">
                          <div
                            className="avatar"
                            style={{
                              width: 34,
                              height: 34,
                              fontSize: '0.7rem',
                              background: 'linear-gradient(135deg, var(--color-blue), #1e40af)',
                            }}
                          >
                            {t.userInitials}
                          </div>
                          <span className="settings__token-name">{t.userName}</span>
                        </div>
                        <span className="settings__token-dept">{t.userSpecialization}</span>
                        <div className="settings__token-value">
                          <div className="settings__token-display">
                            {t.visible ? (
                              <span className="settings__token-chars">{t.token}</span>
                            ) : (
                              <span className="settings__token-hidden">••••</span>
                            )}
                            <button
                              className="btn-icon"
                              onClick={() => toggleVisibility(t.userId)}
                            >
                              {t.visible ? <EyeOff size={13} /> : <Eye size={13} />}
                            </button>
                          </div>
                        </div>
                        <span className="settings__token-date">
                          <Calendar size={12} />
                          {formatDate(t.issuedDate)}
                        </span>
                        <span className={`badge ${t.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>
                          {t.status}
                        </span>
                        <div className="settings__token-actions">
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => handleCopy(t.token, t.userId)}
                          >
                            {copiedId === t.userId ? (
                              <CheckCheck size={13} style={{ color: 'var(--color-lime)' }} />
                            ) : (
                              <Copy size={13} />
                            )}
                          </button>
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={() => handleReissue(t.userId)}
                            disabled={generatingId === t.userId}
                          >
                            <RefreshCw
                              size={13}
                              className={generatingId === t.userId ? 'settings__spin' : ''}
                            />
                            Re-issue
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'system' && (
        <div className="settings__system animate-fade-in">
          <div className="card">
            <div className="card-header">
              <h3>System Settings</h3>
            </div>
            <div className="card-body">
              <div className="settings__system-grid">
                {[
                  { label: 'Platform Name', value: 'ChenaiXR EndoNurseVR LMS', editable: false },
                  { label: 'Version', value: '1.0.0', editable: false },
                  { label: 'Max Content Upload Size', value: '5 GB', editable: true },
                  { label: 'Token Expiry', value: 'Never (manual re-issue)', editable: false },
                  { label: 'Supported Platforms', value: 'Oculus, HTC Vive, PICO', editable: true },
                  { label: 'Default Language', value: 'English', editable: true },
                ].map(({ label, value, editable }) => (
                  <div key={label} className="settings__system-row">
                    <span className="settings__system-label">{label}</span>
                    <div className="settings__system-value">
                      {editable ? (
                        <input className="form-input" defaultValue={value} style={{ maxWidth: 220 }} />
                      ) : (
                        <span className="settings__system-text">{value}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="profile__form-actions" style={{ marginTop: 'var(--space-5)' }}>
                <button className="btn btn-primary">
                  Save System Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Settings;
